<section class="relative flex flex-col items-center w-full px-2 sm:px-3 min-w-0 overflow-x-hidden">
  <div class="relative flex flex-col w-full max-w-5xl gap-6">
    <h1 class="text-xl sm:text-2xl font-bold text-gray-800">Dossier fiscal</h1>

    <section class="relative flex flex-col lg:flex-row gap-4 sm:gap-6 min-w-0">
      <div class="flex flex-col bg-white rounded-md py-5 px-4 sm:py-6 sm:px-6 shadow-xl lg:w-1/3 min-w-0">
        <div class="flex items-center gap-2 pb-4">
          <lucide-icon [name]="Landmark" size="20" class="text-gray-700 shrink-0"></lucide-icon>
          <h2 class="text-gray-700 text-base sm:text-lg font-semibold">Informations fiscales</h2>
        </div>
        <div class="space-y-3 text-sm flex-1">
          <div class="flex flex-col gap-1">
            <span class="text-gray-500 text-xs uppercase tracking-wide font-medium">Dette fiscale</span>
            @if (company().detteFiscale) {
              <span class="font-medium text-gray-800 break-words">{{ company().detteFiscale }}</span>
            } @else if (company().dateInfoFiscale) {
              <span class="text-gray-400 italic flex items-center gap-1">
                <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                Information indisponible
              </span>
            } @else if (hasConfiguredCredentials('impots')) {
              <span class="text-amber-600 italic flex items-center gap-1">
                <lucide-icon [name]="Clock" size="13"></lucide-icon>
                Disponible dès demain
              </span>
            } @else {
              <span class="text-gray-400 italic flex items-center gap-1">
                <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                Identifiants Impots.gouv requis
              </span>
            }
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-gray-500 text-xs uppercase tracking-wide font-medium">Régime d'imposition</span>
            @if (company().regimeImposition) {
              <span class="font-medium text-gray-800 break-words">{{ company().regimeImposition }}</span>
            } @else if (company().dateInfoFiscale) {
              <span class="text-gray-400 italic flex items-center gap-1">
                <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                Information indisponible
              </span>
            } @else if (hasConfiguredCredentials('impots')) {
              <span class="text-amber-600 italic flex items-center gap-1">
                <lucide-icon [name]="Clock" size="13"></lucide-icon>
                Disponible dès demain
              </span>
            } @else {
              <span class="text-gray-400 italic flex items-center gap-1">
                <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                Identifiants Impots.gouv requis
              </span>
            }
          </div>
          @if (company().dateProvigis) {
            <div class="flex flex-col gap-1">
              <span class="text-gray-500 text-xs uppercase tracking-wide font-medium">Dernière mise à jour Provigis</span>
              <span class="font-medium text-gray-800">{{ company().dateProvigis | date: "dd/MM/yyyy à HH:mm" }}</span>
            </div>
          }
          @if (company().dateInfoFiscale) {
            <div class="pt-2 border-t border-gray-100">
              <span class="text-xs text-gray-400">Mis à jour le {{ company().dateInfoFiscale | date: "dd/MM/yyyy à HH:mm" }}</span>
            </div>
          }
        </div>
      </div>

      <div class="flex flex-col bg-white rounded-md py-5 px-4 sm:py-6 sm:px-6 shadow-xl lg:w-2/3 min-w-0">
        <div class="flex items-center gap-2 pb-4">
          <lucide-icon [name]="Calendar" size="20" class="text-gray-700 shrink-0"></lucide-icon>
          <h2 class="text-gray-700 text-base sm:text-lg font-semibold">Calendrier fiscal</h2>
        </div>
        @if (taxCalendar().length === 0) {
          <div class="flex flex-col items-center justify-center py-6 text-center">
            <lucide-icon [name]="Calendar" size="32" class="mb-2 text-gray-300"></lucide-icon>
            @if (company().dateInfoFiscale) {
              <span class="text-sm text-gray-500 font-medium">Information indisponible</span>
            } @else if (hasConfiguredCredentials('impots')) {
              <span class="text-sm text-amber-600 font-medium">Disponible dès demain</span>
            } @else {
              <span class="text-sm text-gray-500">Identifiants Impots.gouv requis</span>
            }
          </div>
        } @else {
          <ul class="md:hidden divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
            @for (entry of taxCalendar(); track entry.id) {
              <li class="p-4 flex flex-col gap-2">
                <p class="text-sm font-medium text-gray-800 break-words">{{ entry.periodeImposition }}</p>
                <p class="text-xs text-gray-600">{{ entry.declarationType }}</p>
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <span class="text-xs text-gray-500">Échéance {{ entry.dateLimite }}</span>
                  <span class="text-xs px-2 py-0.5 rounded font-medium"
                        [class]="entry.etatPeriode?.toLowerCase()?.includes('soldé') || entry.etatPeriode?.toLowerCase()?.includes('solde')
                          ? 'bg-green-100 text-green-700'
                          : entry.etatPeriode?.toLowerCase()?.includes('retard')
                            ? 'bg-red-100 text-red-700'
                            : 'bg-secondary/10 text-secondary'">
                    {{ entry.etatPeriode }}
                  </span>
                </div>
              </li>
            }
          </ul>

          <div class="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-gray-50 text-left text-gray-600">
                  <th class="px-4 py-2.5 font-medium">Période d'imposition</th>
                  <th class="px-4 py-2.5 font-medium">Type de déclaration</th>
                  <th class="px-4 py-2.5 font-medium">Date limite</th>
                  <th class="px-4 py-2.5 font-medium">État</th>
                </tr>
              </thead>
              <tbody>
                @for (entry of taxCalendar(); track entry.id) {
                  <tr class="border-t border-gray-100 hover:bg-gray-50">
                    <td class="px-4 py-3 font-medium text-gray-800">{{ entry.periodeImposition }}</td>
                    <td class="px-4 py-3 text-gray-600">{{ entry.declarationType }}</td>
                    <td class="px-4 py-3 text-gray-600 whitespace-nowrap">{{ entry.dateLimite }}</td>
                    <td class="px-4 py-3">
                      <span class="text-xs px-2 py-0.5 rounded font-medium"
                            [class]="entry.etatPeriode?.toLowerCase()?.includes('soldé') || entry.etatPeriode?.toLowerCase()?.includes('solde')
                              ? 'bg-green-100 text-green-700'
                              : entry.etatPeriode?.toLowerCase()?.includes('retard')
                                ? 'bg-red-100 text-red-700'
                                : 'bg-secondary/10 text-secondary'">
                        {{ entry.etatPeriode }}
                      </span>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </section>
  </div>
</section>
